/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */
import {
  ISubmitCsvFileStatus,
  IUploadCSVFileRequest,
  IUploadCsvFileValidation,
} from "Interfaces/SA/IUploadCSVFileRequest";
import { ISubmitCSVFileRequest } from "Interfaces/SA/ISubmitCSVFileRequest";
import {
  SA_CSV_FILE_UPLOAD,
  SA_CSV_FILE_UPLOAD_SUCCESS,
  SA_CSV_FILE_UPLOAD_FAIL,
  SA_CSV_FILE_SUBMIT,
  SA_CSV_FILE_SUBMIT_SUCCESS,
  SA_CSV_FILE_SUBMIT_FAIL,
  SA_CSV_FILE_UPLOAD_RESET,
} from "./UpdateSARegistrationStatusActionType";

/**
 * Action to upload csv file.
 * @interface
 */
export interface IUploadCsvFile {
  type: typeof SA_CSV_FILE_UPLOAD;
  uploadCsv: IUploadCSVFileRequest;
}

/**
 * Action to upload csv file success.
 * @interface
 */
export interface IUploadCsvFileSuccess {
  type: typeof SA_CSV_FILE_UPLOAD_SUCCESS;
  validationResponse: IUploadCsvFileValidation;
}

/**
 * Action to upload csv file fail.
 * @interface
 */
export interface IUploadCsvFileFail {
  type: typeof SA_CSV_FILE_UPLOAD_FAIL;
  errorMessage: string;
}
/**
 * Action to upload csv file fail reset.
 * @interface
 */
export interface IUploadCsvFileReset {
  type: typeof SA_CSV_FILE_UPLOAD_RESET;
}

/**
 * Action to submit csv file.
 * @interface
 */
export interface ISubmitCsvFile {
  type: typeof SA_CSV_FILE_SUBMIT;
  fileId: ISubmitCSVFileRequest;
}
/**
 * Action to submit csv file success.
 * @interface
 */
export interface ISubmitCsvFileSuccess {
  type: typeof SA_CSV_FILE_SUBMIT_SUCCESS;
  submitResponse: ISubmitCsvFileStatus;
}
/**
 * Action to submit csv file fail.
 * @interface
 */
export interface ISubmitCsvFileFail {
  type: typeof SA_CSV_FILE_SUBMIT_FAIL;
  errorMessage: string;
}

/**
 * Action creator for SA_CSV_FILE_UPLOAD.
 */
export function uploadCsvFile(uploadCsv: IUploadCSVFileRequest): IUploadCsvFile {
  return {
    type: SA_CSV_FILE_UPLOAD,
    uploadCsv,
  };
}
/**
 * Action creator for SA_CSV_FILE_UPLOAD_SUCCESS.
 */
export function uploadCsvFileSuccess(
  validationResponse: IUploadCsvFileValidation
): IUploadCsvFileSuccess {
  return {
    type: SA_CSV_FILE_UPLOAD_SUCCESS,
    validationResponse,
  };
}

/**
 * Action creator for SA_CSV_FILE_UPLOAD_SUCCESS.
 * @param validationResponse
 * @returns Response Object.
 */
export function uploadCsvFileBadRequest(
  validationResponse: IUploadCsvFileValidation
): IUploadCsvFileSuccess {
  return {
    type: SA_CSV_FILE_UPLOAD_SUCCESS,
    validationResponse,
  };
}

/**
 * Action creator for SA_CSV_FILE_UPLOAD_FAIL.
 * @param errorMessage The error message.
 */
export function uploadCsvFileFail(errorMessage: string): IUploadCsvFileFail {
  return {
    type: SA_CSV_FILE_UPLOAD_FAIL,
    errorMessage,
  };
}

/**
 * Action creator for SA_CSV_FILE_UPLOAD_RESET.
 * @param errorMessage The error message.
 */
export function uploadCsvFileReset(): IUploadCsvFileReset {
  return {
    type: SA_CSV_FILE_UPLOAD_RESET,
  };
}

/**
 * Action creator for SA_CSV_FILE_SUBMIT.
 */
export function submitCsvFile(fileId: ISubmitCSVFileRequest): ISubmitCsvFile {
  return {
    type: SA_CSV_FILE_SUBMIT,
    fileId,
  };
}
/**
 * Action creator for SA_CSV_FILE_SUBMIT_SUCCESS.
 */
export function submitCsvFileSuccess(submitResponse: ISubmitCsvFileStatus): ISubmitCsvFileSuccess {
  return {
    type: SA_CSV_FILE_SUBMIT_SUCCESS,
    submitResponse,
  };
}

/**
 * Action creator for SA_CSV_FILE_SUBMIT_FAIL.
 * @param errorMessage The error message.
 */
export function submitCsvFileFail(errorMessage: string): ISubmitCsvFileFail {
  return {
    type: SA_CSV_FILE_SUBMIT_FAIL,
    errorMessage,
  };
}
export type UpdateSARegistrationActionTypes =
  | IUploadCsvFile
  | IUploadCsvFileSuccess
  | IUploadCsvFileFail
  | IUploadCsvFileReset
  | ISubmitCsvFile
  | ISubmitCsvFileSuccess
  | ISubmitCsvFileFail;
